import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Badge, Button, Card, CardBody, Container, Input, ModalBody, Spinner, Table } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import DraftModal from "pages/EventsManagement/components/Tabs/Logistics/DraftModal"
import { actorName, logTime, plural } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { COURIER_UNCONFIGURED, LABELS_FAILED, cancellationLabel, cargusLabelled, carriedItems, courierAlertNames, courierStatusLabel, dayLabel, emailColor,
  emailSummaryLabel, messageName, shipmentTitle, transportName, weightLabel } from "pages/EventsManagement/components/Tabs/Logistics/shipmentLabels"
import { shipmentReadyToShip, shipmentShipped } from "pages/EventsManagement/components/Tabs/Logistics/sheetState"
import useLogisticsOperation, { UNCONFIRMED_CHANGE } from "pages/EventsManagement/components/Tabs/Logistics/useLogisticsOperation"
import OrganizerLink from "pages/EventsManagement/components/Tabs/Logistics/OrganizerLink"
import useXlsxDownload from "pages/EventsManagement/components/Tabs/Logistics/useXlsxDownload"
import { Choice, Period, ReadError, useAddressRead } from "../addressFilters"
import { eventStateLabel } from "../Sheets/sheetLabels"
import { FILTERS, exportParams, returnedLabel, shipmentLabel, shown, transports } from "./shipmentListLabels"
import "pages/EventsManagement/components/Tabs/Logistics/logistics.scss"

const FAILED = "The shipments could not be loaded."
// A refresh Cargus could not answer changes no status.
const REFRESH_UNAVAILABLE = "Cargus cannot be reached now: the courier statuses stay as they were. Try again later."
// A refresh Events did not confirm is no failure: it may still run, and the statuses the list shows may be the previous ones. A refresh is
// not logged, so the notice points to no action logs.
const REFRESH_UNCONFIRMED = "The refresh was not confirmed within a minute. The list shows the last statuses read; try again in a few minutes."
// The time of the pickup point at Câmpina.
const HOME_ZONE = "Europe/Bucharest"

// How a shipment left, or, processed, how it will leave: its transport, whether its AWB was entered by hand, the AWB with its tracking link,
// when the courier picks up one Cargus generated, the courier's last status with its alerts (US 108–110; only the link on a server without
// a courier), and the note; in preparation, its last cancellation. A supplementary shipment names what it carries, which for the main
// shipment is its sheet.
const Transport = ({ shipment, timezone, hasCourier }) => <>
  {shipmentShipped(shipment.status) || shipmentReadyToShip(shipment.status) ? <><strong>{transportName(shipment)}</strong>
    {shipment.awbSource === "manual" && <span className="text-muted"> · manual AWB</span>}
    {shipmentReadyToShip(shipment.status) && <span className="text-muted"> · not shipped yet</span>}</>
    : <span className="text-muted">Not shipped yet</span>}
  {shipment.awb && <div className="small">{shipment.trackingUrl
    ? <a href={shipment.trackingUrl} target="_blank" rel="noopener noreferrer">AWB {shipment.awb}</a> : `AWB ${shipment.awb}`}</div>}
  {shipment.pickupDay && <div className="small text-muted">{`Estimated pickup: ${dayLabel(shipment.pickupDay)}`}</div>}
  {hasCourier && shipment.courierStatus && <div className="small text-muted">{courierStatusLabel(shipment.courierStatus)}</div>}
  {hasCourier && shipment.courierAlerts?.map(alert => <Badge key={alert} color="soft-danger" className="mr-1">{courierAlertNames[alert]}</Badge>)}
  {shipment.note && <div className="small text-muted">{shipment.note}</div>}
  {shipment.cancellation && <div className="small text-muted">{cancellationLabel(shipment.cancellation, timezone)}</div>}
  {shipment.kind === "supplementary" && <div className="small text-muted">{carriedItems(shipment).join(" · ")}</div>}
</>

// A shipment's email summed up (US 170, ticket 27 Q11): its worst state, named unless it is the shipment email (the requirements, the AWB
// email, the cancellation's); a dash while nothing is recorded.
const Email = ({ email }) => (email?.status ? <><Badge color={`soft-${emailColor(email.status)}`}>{emailSummaryLabel(email)}</Badge>
  {email.message !== "shipment" && <div className="small text-muted">{messageName(email.message)}</div>}</> : <span className="text-muted">—</span>)

// One row per shipment, in pairs so that no column scrolls (ticket 22 Q5): the event and the shipment, the club and the organizer, how it
// left, its parcels and weight, what came back of it and its email; the event's name opens its Logistics tab, where the email is resent,
// and the organizer's name the organizer's profile.
// A Cargus shipment that left is chosen for its label (`chosen`, `onChoose`).
const ShipmentsTable = ({ shipments, chosen, onChoose, hasCourier }) => <Table size="sm" className="logistics-stack mb-0">
  <thead><tr>
    <th>Event · shipment</th><th>Club · organizer</th><th>Transport</th><th className="text-right">Parcels · weight</th><th>Return</th><th>Email</th>
  </tr></thead>
  <tbody>{shipments.map(shipment => {
    const { event } = shipment
    return <tr key={`${shipment.kind}-${shipment.id}`}>
      <td>{cargusLabelled(shipment) && <Input type="checkbox" className="position-static mr-2 align-middle" checked={chosen.includes(shipment.id)}
        aria-label={`Label of ${shipmentTitle(shipment)} · ${event.name || "Unnamed event"}`} onChange={() => onChoose(shipment.id)} />}
        <Link to={`/events/${event.id}/logistics`}><strong>{event.name || "Unnamed event"}</strong></Link>
        {eventStateLabel(event.status) && <Badge color="soft-secondary" className="ml-1">{eventStateLabel(event.status)}</Badge>}
        <div className="small text-muted">{shipmentLabel(shipment)}{event.eventId ? ` · #${event.eventId}` : ""}</div></td>
      <td data-label="Club · organizer">{event.club?.name || "No club"}
        <div className="small text-muted"><OrganizerLink organizer={event.organizer}>{event.organizer?.name || "No organizer"}</OrganizerLink></div></td>
      <td data-label="Transport"><Transport shipment={shipment} timezone={event.timezone} hasCourier={hasCourier} /></td>
      <td className="text-right" data-label="Parcels · weight">{[shipment.parcels && plural(shipment.parcels, "parcel"), weightLabel(shipment.weight)]
        .filter(Boolean).join(" · ") || "—"}</td>
      <td data-label="Return">{shipment.returned.length ? <><Badge color="soft-info">Yes</Badge>
        <div className="small text-muted">{returnedLabel(shipment)}</div></> : <span className="text-muted">No</span>}</td>
      <td data-label="Email"><Email email={shipment.email} /></td>
    </tr>
  })}</tbody>
</Table>

// „Launch pickup order”: today's pickup order at Câmpina launched by hand, from now until the pickup point's hour, when it did not close by
// itself. Saved, the dialog closes; refused, it says why.
function LaunchPickupDialog({ closing, pending, onLaunch, onClose }) {
  const [failure, setFailure] = useState(null)
  const launch = () => {
    setFailure(null)
    onLaunch().then(result => {
      if (result?.status === "saved") onClose()
      else if (result && result.status !== "cancelled") setFailure(result.status === "unknown" ? UNCONFIRMED_CHANGE : result.message)
    })
  }
  return <DraftModal id="logistics-pickup-title" title="Launch today's pickup order" dirty={false} onClose={onClose}
    actions={<Button color="primary" disabled={pending} onClick={launch}>{pending ? "Launching…" : "Launch pickup order"}</Button>}>
    <ModalBody>
      <p className="mb-2">{`Cargus is asked to pick up today's parcels at Câmpina, from now until ${closing}.`}</p>
      <p className="small text-muted mb-0">Use it only when the order did not close by itself at the pickup point's hour: it can be launched once a day.</p>
      {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    </ModalBody>
  </DraftModal>
}

// Today's pickup at Câmpina (US 107, ticket 20 Q4): the day's order closes by itself at the pickup point's hour; when it did not, a
// SuperAdmin launches it by hand once a day, and the page says who did, when and Cargus's order. On a server without a courier
// (`hasCourier` false), the launch shows disabled.
function Pickup({ pickup, hasCourier, pending, onLaunch }) {
  const [open, setOpen] = useState(false)
  const { closing, launched } = pickup
  return <Card tag="section" aria-label="Pickup at Câmpina"><CardBody className="d-flex flex-wrap align-items-center">
    <div className="mr-3">
      <strong>Pickup at Câmpina</strong>
      <div className="small text-muted">{`The day's pickup order closes by itself at ${closing}.`}</div>
      {launched && <div className="small">{`Launched by hand by ${actorName(launched.by)}, ${logTime(launched.at, HOME_ZONE)} · Cargus order ${launched.order}`}</div>}
      {!hasCourier && <div className="small text-muted">{`${COURIER_UNCONFIGURED}: no label, status or pickup can be asked of it.`}</div>}
    </div>
    {!launched && <Button color="light" size="sm" disabled={pending || !hasCourier} onClick={() => setOpen(true)}>
      <i className="bx bx-package mr-1" aria-hidden="true" />Launch pickup order
    </Button>}
    {open && <LaunchPickupDialog closing={closing} pending={pending} onLaunch={onLaunch} onClose={() => setOpen(false)} />}
  </CardBody></Card>
}

// Logistics → Shipments (US 106–107, 135–136, 170, tickets 18 Q2, 20, 22 Q5 and 27 Q11): the shipments that left in a period, the last 30
// days by default, then those in preparation, filtered by club, transport and what to show, with the filters in the address, each with its
// email, and their XLSX; the labels of the Cargus shipments chosen, as one PDF; the courier's status of the Cargus shipments, read every two
// hours and at once with „Refresh courier status” (not logged), with its alerts; and today's pickup at Câmpina, launched by hand when it
// did not close by itself.
export default function LogisticsShipments() {
  const { params, answer, ready, error, retry, reload, setParam, replaceSearch } = useAddressRead("/admin/logistics/shipments", FILTERS, FAILED)
  const download = useXlsxDownload()
  const labels = useXlsxDownload(LABELS_FAILED)
  // The pickup's launch belongs to no event, as Events answers it.
  const operation = useLogisticsOperation(undefined)
  const [chosen, setChosen] = useState([])
  const clubs = answer?.options?.clubs || []
  // Whether the server has a courier for the labels and the pickup (Events answers it with the list): without one, they show disabled.
  const hasCourier = answer?.courier?.configured !== false

  // The XLSX of the filter the page shows.
  const exportXlsx = () => ready && download.download("/exports/logistics/shipments", { params: exportParams(answer.filter) },
    `logistics-shipments-${answer.filter.from}-${answer.filter.to}.xlsx`)
  // The labels chosen among the Cargus shipments the page shows, in its order.
  const labelling = ready ? answer.shipments.filter(shipment => cargusLabelled(shipment) && chosen.includes(shipment.id)).map(shipment => shipment.id) : []
  const choose = id => setChosen(previous => (previous.includes(id) ? previous.filter(other => other !== id) : [...previous, id]))
  const downloadLabels = () => labels.download("/exports/logistics/labels", { params: { shipments: labelling.join(",") } }, "cargus-labels.pdf")
  const launchPickup = () => operation.run("launchPickup", "/admin/logistics/pickup", { action: "launchPickup" }).then(result => {
    if (["saved", "unknown"].includes(result?.status)) reload()
    return result
  })
  // „Refresh now” (US 108): the courier's status of the recent Cargus shipments read at once; the page reads them again, or says why not,
  // and says in amber when the refresh was not confirmed.
  const [refreshFailure, setRefreshFailure] = useState(null)
  const [refreshNotice, setRefreshNotice] = useState(null)
  const refreshStatus = () => {
    setRefreshFailure(null)
    setRefreshNotice(null)
    operation.run("refreshCourierStatus", "/admin/logistics/courier-status", { action: "refreshCourierStatus" }).then(result => {
      if (["saved", "unknown"].includes(result?.status)) {
        reload()
        if (result.status === "unknown") setRefreshNotice(REFRESH_UNCONFIRMED)
      } else if (result && result.status !== "cancelled") setRefreshFailure(result.code === "LOGISTICS_COURIER_UNAVAILABLE" ? REFRESH_UNAVAILABLE : result.message)
    })
  }

  return <div className="page-content logistics-shipments">
    <Container fluid>
      <Breadcrumbs title="Logistics" breadcrumbItem="Shipments" path="/logistics/shipments" />
      <div className="logistics-toolbar">
        <Button size="sm" color="light" className="mr-2" onClick={downloadLabels} disabled={!hasCourier || !labelling.length || labels.busy}>
          <i className="bx bx-printer mr-1" aria-hidden="true" />{labels.busy ? "Preparing labels…" : `Download labels${labelling.length ? ` (${labelling.length})` : ""}`}
        </Button>
        <Button size="sm" color="light" className="mr-2" onClick={refreshStatus} disabled={!hasCourier || operation.pending}>
          <i className="bx bx-refresh mr-1" aria-hidden="true" />Refresh courier status
        </Button>
        <Button size="sm" color="light" onClick={exportXlsx} disabled={!ready || download.busy}>
          <i className="bx bx-download mr-1" aria-hidden="true" />{download.busy ? "Preparing XLSX…" : "Export XLSX"}
        </Button>
      </div>
      {refreshFailure && <Alert color="danger" isOpen toggle={() => setRefreshFailure(null)}>{refreshFailure}</Alert>}
      {refreshNotice && <Alert color="warning" isOpen toggle={() => setRefreshNotice(null)}>{refreshNotice}</Alert>}
      {download.error && <Alert color="danger" isOpen toggle={download.dismiss}>{download.error}</Alert>}
      {labels.error && <Alert color="danger" isOpen toggle={labels.dismiss}>{labels.error}</Alert>}
      <ReadError error={error} retry={retry} />
      {answer?.pickup && <Pickup pickup={answer.pickup} hasCourier={hasCourier} pending={operation.pending} onLaunch={launchPickup} />}

      <Card tag="section" aria-label="Filters"><CardBody className="logistics-filters">
        <Period id="logistics-shipments-period" label="Period (shipment date)" from={params.get("from") || answer?.filter?.from || ""}
          to={params.get("to") || answer?.filter?.to || ""} onChange={setParam} />
        <Choice id="logistics-shipments-club" label="Club" all="All clubs" missing="Club without shipments in this period" value={params.get("clubId") || ""}
          choices={clubs.map(club => ({ value: club.id, label: club.name || "Unnamed club" }))} onChange={value => setParam("clubId", value)} />
        <Choice id="logistics-shipments-transport" label="Transport" all="All" missing={params.get("transport")} value={params.get("transport") || ""}
          choices={transports} onChange={value => setParam("transport", value)} />
        <Choice id="logistics-shipments-show" label="Show" all="All shipments" missing={params.get("show")} value={params.get("show") || ""}
          choices={shown} onChange={value => setParam("show", value)} />
        <Button color="link" size="sm" className="px-0" onClick={() => replaceSearch(new URLSearchParams())}>Reset filters</Button>
      </CardBody></Card>

      <Card tag="section" aria-label="Shipments"><CardBody>
        {ready && <p className="text-muted small">{plural(answer.shipments.length, "shipment")} · the earliest first, those in preparation last</p>}
        {!ready && !error && <div className="text-center my-3"><Spinner color="primary" size="sm" /></div>}
        {ready && (answer.shipments.length ? <ShipmentsTable shipments={answer.shipments} chosen={chosen} onChoose={choose} hasCourier={hasCourier} />
          : <p className="text-muted mb-0">No shipment matches these filters.</p>)}
      </CardBody></Card>
    </Container>
  </div>
}
